// 데모 계정(username: demo)이 화면에 뿌리는 분석 데이터.
// BE 를 전혀 타지 않고 프런트에서만 만들어 내며, 실계정 응답과 같은 필드 이름을 쓴다.
// React 를 import 하지 않는 순수 모듈이라 테스트에서 그대로 부를 수 있다.

const asset = (filename) => `${process.env.PUBLIC_URL || ""}/demo/${filename}`;

// 라벨은 kind 로, 요약은 summaryKey 로 i18n 키를 잡는다. 화면 문구(label/summary)는
// 저장하지 않고 노출 시점(localizeResult)에 현재 언어로 만든다 — 언어 전환·새로고침에도 맞다.
//
// 작물마다 샘플 세트가 따로 있다(계정이 정한다 — DemoScenarioContext.DEMO_ACCOUNTS).
//   cabbage : demo 계정. 정상 3 + 무름병 3 + 균핵병 2.
//   rice    : demo2 계정. 정상 3장뿐 — 병해 없이 전 칸 정상으로 보여 준다(10-21 결정). 사진은
//             public/demo/rice-demo-1~3.jpg, Grad-CAM 은 같은 장면의 rice-demo-N-gradcam.jpg.
//             병해 풀이 비면 CELL_PROFILES 의 병해 장 수는 0 이 되고 칸은 전부 OK 등급이다.
// 어느 세트든 병해 5장 · 정상 3장을 넘지 않아야 한 칸 안에서 사진이 중복되지 않는다(CELL_PROFILES).
export const DEFAULT_DEMO_CROP = "cabbage";

const SAMPLE_SETS = {
  cabbage: [
  {
    kind: "normal",
    summaryKey: "demo.sample.summary.normal.1",
    tone: "normal",
    confidence: 98,
    crop: asset("cabbage-normal-demo-v5.png"),
    gradcam: asset("cabbage-normal-demo-v5-gradcam.png"),
  },
  {
    kind: "soft-rot",
    summaryKey: "demo.sample.summary.softRot.1",
    tone: "danger",
    confidence: 94,
    crop: asset("cabbage-soft-rot-demo-v2.png"),
    gradcam: asset("cabbage-soft-rot-demo-v2-gradcam.png"),
  },
  {
    kind: "sclerotinia",
    summaryKey: "demo.sample.summary.sclerotinia.1",
    tone: "danger",
    confidence: 91,
    crop: asset("cabbage-sclerotinia-demo-v2.png"),
    gradcam: asset("cabbage-sclerotinia-demo-v2-gradcam.png"),
  },
  {
    kind: "normal",
    summaryKey: "demo.sample.summary.normal.2",
    tone: "normal",
    confidence: 97,
    crop: asset("cabbage-normal-demo-v6.png"),
    gradcam: asset("cabbage-normal-demo-v6-gradcam.png"),
  },
  {
    kind: "soft-rot",
    summaryKey: "demo.sample.summary.softRot.2",
    tone: "danger",
    confidence: 92,
    crop: asset("cabbage-soft-rot-demo-v3.png"),
    gradcam: asset("cabbage-soft-rot-demo-v3-gradcam.png"),
  },
  {
    kind: "sclerotinia",
    summaryKey: "demo.sample.summary.sclerotinia.2",
    tone: "danger",
    confidence: 89,
    crop: asset("cabbage-sclerotinia-demo-v3.png"),
    gradcam: asset("cabbage-sclerotinia-demo-v3-gradcam.png"),
  },
  {
    kind: "normal",
    summaryKey: "demo.sample.summary.normal.3",
    tone: "normal",
    confidence: 96,
    crop: asset("cabbage-normal-demo-v7.png"),
    gradcam: asset("cabbage-normal-demo-v7-gradcam.png"),
  },
  {
    kind: "soft-rot",
    summaryKey: "demo.sample.summary.softRot.3",
    tone: "danger",
    confidence: 90,
    crop: asset("cabbage-soft-rot-demo-v4.png"),
    gradcam: asset("cabbage-soft-rot-demo-v4-gradcam.png"),
  },
  ],
  rice: [
  {
    kind: "normal",
    summaryKey: "demo.sample.summary.rice.normal.1",
    tone: "normal",
    confidence: 98,
    crop: asset("rice-demo-1.jpg"),
    gradcam: asset("rice-demo-1-gradcam.jpg"),
  },
  {
    kind: "normal",
    summaryKey: "demo.sample.summary.rice.normal.2",
    tone: "normal",
    confidence: 97,
    crop: asset("rice-demo-2.jpg"),
    gradcam: asset("rice-demo-2-gradcam.jpg"),
  },
  {
    kind: "normal",
    summaryKey: "demo.sample.summary.rice.normal.3",
    tone: "normal",
    confidence: 96,
    crop: asset("rice-demo-3.jpg"),
    gradcam: asset("rice-demo-3-gradcam.jpg"),
  },
  ],
};

export const DEMO_CROPS = Object.keys(SAMPLE_SETS);
export const normalizeDemoCrop = (crop) => (SAMPLE_SETS[crop] ? crop : DEFAULT_DEMO_CROP);

// ── 칸 등급 ──────────────────────────────────────────────────────────
// 실계정은 BE /cell-status 가 칸별 등급·장 수를 내려주고 지도가 그 값으로 칸을 칠한다.
// 데모도 같은 모양(CellStatusResponse.CellSummary)을 만들어 같은 렌더 경로를 태운다.
// 경계값은 BE 기본값(app.diagnosis.high/mid-threshold)과 맞춘다.
export const DEMO_GRADE_THRESHOLDS = { high: 80, mid: 30 };

const samplePools = (crop) => {
  const set = SAMPLE_SETS[normalizeDemoCrop(crop)];
  return {
    normals: set.filter((sample) => sample.kind === "normal"),
    diseases: set.filter((sample) => sample.kind !== "normal"),
  };
};

// 등급별 확률 구간. 주의(mid) 구간이 비면 지도가 빨강·초록 두 색으로만 나와서
// 범례 세 줄 중 한 줄이 캡처에서 죽는다.
const CONF_BANDS = { high: [84, 95], mid: [38, 72], ok: [93, 98] };

// 칸 하나의 구성. 개수를 범위로 두고 칸마다 다르게 뽑는다.
// 고정 개수로 앞에서부터 자르면 같은 등급의 칸이 전부 같은 사진 목록이 된다
// (칸을 두 개 열어 보면 바로 티가 난다).
// 병해 풀 5장·정상 풀 3장을 넘지 않아야 한 칸 안에서 중복이 안 생긴다.
const CELL_PROFILES = {
  HIGH: { high: [1, 2], mid: [2, 3], ok: [2, 3] },
  MID: { high: [0, 0], mid: [2, 3], ok: [2, 3] },
  OK: { high: [0, 0], mid: [0, 0], ok: [2, 3] },
};

const pickCount = (rand, [lo, hi]) => lo + Math.round(rand() * (hi - lo));

// 고정 난수로 도는 Fisher-Yates. 칸마다 다른 조합이 나오되 새로고침에는 안 바뀐다.
const shuffled = (rand, items) => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

// 등급 배치는 무작위가 아니라 순번으로 정한다. 새로고침해도 같은 칸이 같은 색이라
// 같은 화면을 몇 번이든 다시 캡처할 수 있다. 30칸 기준 심각 4 / 주의 9 / 정상 17.
const profileForIndex = (index) => {
  const m = index % 7;
  if (m === 3) return "HIGH";
  if (m === 1 || m === 5) return "MID";
  return "OK";
};

// cellId 로 고정되는 난수(LCG). 확률 숫자만 칸마다 달라 보이게 하는 용도다.
const seededRandom = (seed) => {
  let state = (Math.abs(Math.trunc(seed)) % 2147483646) + 1;
  return () => {
    state = (state * 48271) % 2147483647;
    return (state - 1) / 2147483646;
  };
};

const confIn = (rand, band) => band[0] + Math.round(rand() * (band[1] - band[0]));

// 지도 칸 색과 미리보기 칩이 같은 규칙을 써야 "심각 2장인데 칸은 주의" 같은 모순이 없다.
// DroneMapTest 의 segGrade 와 같은 판정이다.
const photoGrade = (photo) => {
  if (!photo || photo.category === "NORMAL") return "ok";
  return photo.conf >= DEMO_GRADE_THRESHOLDS.high ? "high" : "mid";
};

export const buildCellPhotos = (cellId, index, crop = DEFAULT_DEMO_CROP) => {
  const rand = seededRandom(cellId * 131 + index);
  const plan = CELL_PROFILES[profileForIndex(index)];
  const pools = samplePools(crop);

  // 개수와 조합을 칸마다 다르게 뽑는다. 풀을 섞은 뒤 앞에서 가져가므로
  // 한 칸 안에서는 여전히 중복이 없다.
  const highN = pickCount(rand, plan.high);
  const midN = pickCount(rand, plan.mid);
  const okN = pickCount(rand, plan.ok);
  const diseases = shuffled(rand, pools.diseases);
  const normals = shuffled(rand, pools.normals);

  const photoOf = (sample, band) => ({
    crop: sample.crop,
    gradcam: sample.gradcam,
    kind: sample.kind,
    summaryKey: sample.summaryKey,
    conf: confIn(rand, band),
    category: sample.kind === "normal" ? "NORMAL" : "DISEASE",
  });

  // 나쁜 것부터 보여준다. 목록을 열자마자 병해 크롭이 눈에 들어와야 한다.
  return [
    ...diseases.slice(0, highN).map((s) => photoOf(s, CONF_BANDS.high)),
    ...diseases.slice(highN, highN + midN).map((s) => photoOf(s, CONF_BANDS.mid)),
    ...normals.slice(0, okN).map((s) => photoOf(s, CONF_BANDS.ok)),
  ];
};

// 칸을 대표하는 한 장 = 가장 나쁜 장. 칸 머리글의 병명·확률·색이 여기서 나온다.
export const representativePhoto = (photos) => {
  const worst = photos
    .filter((photo) => photoGrade(photo) !== "ok")
    .sort((a, b) => b.conf - a.conf)[0];
  return worst ?? photos[0];
};

// 데모 회차 결과 -> 지도가 쓰는 칸 요약. 실계정 CellStatusResponse.CellSummary 와
// 같은 필드만 담는다. 필드 이름이 하나라도 어긋나면 칸이 조용히 회색으로 남는다.
export const buildDemoCellSummaries = (results) => {
  const cells = {};
  const highCellIds = [];
  (Array.isArray(results) ? results : []).forEach((result) => {
    const photos = Array.isArray(result?.photos) ? result.photos : [];
    if (!photos.length) return;
    const counts = photos.reduce(
      (acc, photo) => {
        acc[photoGrade(photo)] += 1;
        return acc;
      },
      { high: 0, mid: 0, ok: 0 }
    );
    const grade = counts.high ? "HIGH" : counts.mid ? "MID" : "OK";
    if (grade === "HIGH") highCellIds.push(result.cellId);
    cells[result.cellId] = {
      gridCellId: result.cellId,
      grade,
      detectedCount: photos.length,
      highCount: counts.high,
      midCount: counts.mid,
      okCount: counts.ok,
    };
  });
  return { cells, highCellIds, thresholds: DEMO_GRADE_THRESHOLDS };
};

// 저장된 결과(kind/summaryKey)에 현재 언어의 label/summary 를 붙인다.
// 예전에 localStorage 에 남은 기록은 한국어 label/summary 를 그대로 갖고 있으므로 그 값으로 폴백한다.
export const localizeResult = (result, t) => {
  if (!result) return result;
  const label = result.kind ? t(`demo.sample.label.${result.kind}`) : result.label;
  const summary = result.summaryKey ? t(result.summaryKey) : result.summary;
  // 크롭 한 장의 name 이 있어야 미리보기가 등급 칩과 확률 배지를 띄운다
  // (DroneMapTest 의 hasSegmentGrades). 없으면 전부 "정상"으로만 보인다.
  const photos = Array.isArray(result.photos)
    ? result.photos.map((photo) =>
        photo?.kind ? { ...photo, name: t(`demo.sample.label.${photo.kind}`) } : photo
      )
    : result.photos;
  return { ...result, label, summary, photos };
};

export const localizeResults = (results, t) =>
  Array.isArray(results) ? results.map((result) => localizeResult(result, t)) : results;

// 한 칸의 결과 한 건. 실제 비행(startDemoMission)과 미리 심는 회차가 같은 함수를 써야
// "심은 회차만 화면이 다르다" 같은 어긋남이 안 생긴다.
export const buildCellResult = (
  cellId,
  index,
  startedAt,
  captureIntervalMs = 5000,
  // 등급 배치에 쓰는 순번. 촬영 순번(index)과 떼어 두면 회차마다 등급 무늬를
  // 어긋나게 할 수 있다. 안 넘기면 촬영 순번을 그대로 쓴다.
  profileIndex = index,
  // 샘플 세트. 계정이 정한다(demo = cabbage, demo2 = rice).
  crop = DEFAULT_DEMO_CROP
) => {
  const photos = buildCellPhotos(cellId, profileIndex, crop);
  const rep = representativePhoto(photos);
  return {
    cellId,
    sequence: index + 1,
    capturedAt: new Date(startedAt.getTime() + (index + 1) * captureIntervalMs),
    kind: rep.kind,
    summaryKey: rep.summaryKey,
    tone: rep.category === "NORMAL" ? "normal" : "danger",
    confidence: rep.conf,
    // photos 를 못 읽는 예전 경로를 위한 대표 1장
    crop: rep.crop,
    gradcam: rep.gradcam,
    photos,
  };
};
